import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { runInAction } from 'mobx';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useKeepInTouchAccountNotesMutation } from '../../../../../queries/KeepInTouchAgent';
import { useUserPreferencesMutation } from '../../../../../queries/User/useUserPreferencesMutation';
import { Button } from '../../../../components-v2/Button';
import { TextArea } from '../../../../components-v2/TextArea';
import { ChevronDown } from '../../../../components/svgs/icons/ChevronDown';
import { ChevronUp } from '../../../../components/svgs/icons/ChevronUp';
import { styleSheet } from './styles';

interface InstructionsPanelProps {
	defaultExpanded?: boolean;
}

export const InstructionsPanel: React.FC<InstructionsPanelProps> = ({ defaultExpanded = false }) => {
	const userSession = useUserSession();
	const isAdmin = userSession.userRole === Api.EUserRole.Admin || userSession.userRole === Api.EUserRole.SuperAdmin;

	const accountNotes = userSession.account?.preferences?.keepInTouchNotes ?? '';
	const userNotes = userSession.user?.userPreferences?.keepInTouchNotes ?? '';
	const hasAnyNotes = !!accountNotes || !!userNotes;

	const [expanded, setExpanded] = React.useState(defaultExpanded);
	const [editingUser, setEditingUser] = React.useState(false);
	const [editingAccount, setEditingAccount] = React.useState(false);
	const [draftUserNotes, setDraftUserNotes] = React.useState(userNotes);
	const [draftAccountNotes, setDraftAccountNotes] = React.useState(accountNotes);

	const userPrefsMutation = useUserPreferencesMutation({
		onSuccess: () => {
			toast.success('Your instructions saved');
			setEditingUser(false);
			runInAction(() => {
				if (userSession.user?.userPreferences) {
					userSession.user.userPreferences.keepInTouchNotes = draftUserNotes;
				}
			});
		},
		onError: () => {
			toast.error('Failed to save instructions');
		},
	});

	const accountNotesMutation = useKeepInTouchAccountNotesMutation({
		onSuccess: () => {
			toast.success('Account instructions saved');
			setEditingAccount(false);
			runInAction(() => {
				if (userSession.account?.preferences) {
					userSession.account.preferences.keepInTouchNotes = draftAccountNotes;
				}
			});
		},
		onError: () => {
			toast.error('Failed to save account instructions');
		},
	});

	const handleSaveUser = () => {
		userPrefsMutation.mutate({
			preferences: {
				...userSession.user?.userPreferences,
				keepInTouchNotes: draftUserNotes,
			},
		});
	};

	const handleSaveAccount = () => {
		accountNotesMutation.mutate(draftAccountNotes);
	};

	const handleCancelUser = () => {
		setDraftUserNotes(userNotes);
		setEditingUser(false);
	};

	const handleCancelAccount = () => {
		setDraftAccountNotes(accountNotes);
		setEditingAccount(false);
	};

	const activeNotes = userNotes || accountNotes;

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)} onClick={() => setExpanded(!expanded)}>
				<div className={css(styleSheet.headerLeft)}>
					<h3 className={css(styleSheet.headerTitle)}>AI Instructions</h3>
					{hasAnyNotes ? <span className={css(styleSheet.headerBadge)}>Configured</span> : null}
				</div>
				<span className={css(styleSheet.chevron)}>
					{expanded ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
				</span>
			</div>
			{!expanded && activeNotes ? (
				<div style={{ padding: '0 16px 12px' }}>
					<p className={css(styleSheet.preview)}>{activeNotes}</p>
				</div>
			) : null}
			{expanded ? (
				<div className={css(styleSheet.body)}>
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>
							Account Instructions {!isAdmin ? '(set by admin)' : '(applies to all users)'}
						</div>
						{isAdmin && editingAccount ? (
							<>
								<TextArea
									value={draftAccountNotes}
									onChange={e => setDraftAccountNotes(e.target.value)}
									placeholder="e.g., We're an insurance agency. Always prioritize clients with upcoming renewals."
									rows={4}
								/>
								<div className={css(styleSheet.actions)}>
									<Button
										variant='secondary'
										size='sm'
										onClick={handleCancelAccount}
										disabled={accountNotesMutation.isPending}
									>
										Cancel
									</Button>
									<Button size='sm' onClick={handleSaveAccount} isLoading={accountNotesMutation.isPending}>
										Save
									</Button>
								</div>
							</>
						) : accountNotes ? (
							<>
								<p className={css(styleSheet.accountNotes)}>{accountNotes}</p>
								{isAdmin ? (
									<Button
										variant='link'
										size='sm'
										onClick={() => setEditingAccount(true)}
										styles={[styleSheet.editLink]}
									>
										Edit
									</Button>
								) : null}
							</>
						) : isAdmin ? (
							<>
								<p className={css(styleSheet.emptyHint)}>
									Add account-wide instructions to guide the AI for all users.
								</p>
								<Button variant='link' size='sm' onClick={() => setEditingAccount(true)} styles={[styleSheet.editLink]}>
									Add instructions
								</Button>
							</>
						) : (
							<p className={css(styleSheet.emptyHint)}>No account-wide instructions set.</p>
						)}
						{accountNotes && userNotes ? (
							<p className={css(styleSheet.accountNotesHint)}>
								Personal instructions below override account instructions.
							</p>
						) : null}
					</div>
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionLabel)}>Your Instructions</div>
						{editingUser ? (
							<>
								<TextArea
									value={draftUserNotes}
									onChange={e => setDraftUserNotes(e.target.value)}
									placeholder='e.g., I handle the East Coast book of business. Focus on my VC relationships.'
									rows={4}
								/>
								<div className={css(styleSheet.actions)}>
									<Button
										variant='secondary'
										size='sm'
										onClick={handleCancelUser}
										disabled={userPrefsMutation.isPending}
									>
										Cancel
									</Button>
									<Button size='sm' onClick={handleSaveUser} isLoading={userPrefsMutation.isPending}>
										Save
									</Button>
								</div>
							</>
						) : userNotes ? (
							<>
								<p className={css(styleSheet.accountNotes)}>{userNotes}</p>
								<Button variant='link' size='sm' onClick={() => setEditingUser(true)} styles={[styleSheet.editLink]}>
									Edit
								</Button>
							</>
						) : (
							<>
								<p className={css(styleSheet.emptyHint)}>
									Add personal instructions to guide the AI when researching your contacts.
								</p>
								<Button variant='link' size='sm' onClick={() => setEditingUser(true)} styles={[styleSheet.editLink]}>
									Add instructions
								</Button>
							</>
						)}
					</div>
				</div>
			) : null}
		</div>
	);
};
